import { useMemo, type FunctionComponent, type PropsWithChildren } from 'react';

import { PostOfficeProvider as PostOfficeBaseProvider } from '@atlassian/post-office-placement-remote';
import { environment as trelloEnvironment } from '@trello/config';
import { currentLocale } from '@trello/locale';
import { useSharedState } from '@trello/shared-state';

import { useCrossFlowPostOfficeContext } from './CrossFlowPostOfficeProvider';
import { postOfficeEnvironmentSharedState } from './postOfficeEnvironmentSharedState';

const BASE_CONTEXT = {
  current: {
    product: 'trello',
    locale: currentLocale,
  },
};

export const PostOfficeProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}: PropsWithChildren) => {
  const crossFlowPostOfficeContext = useCrossFlowPostOfficeContext();
  const [env] = useSharedState(postOfficeEnvironmentSharedState);
  const effectiveEnv = trelloEnvironment === 'prod' ? 'production' : env;
  const envOverrides =
    effectiveEnv === 'test'
      ? {
          api: {
            pathname: '/gateway/api/post-office/api/v1/placements/',
            origin: 'http://localhost:4224',
          },
        }
      : {};

  const context = useMemo(
    () => ({
      current: {
        ...BASE_CONTEXT.current,
        ...crossFlowPostOfficeContext,
      },
    }),
    [crossFlowPostOfficeContext],
  );

  return (
    <PostOfficeBaseProvider
      context={context}
      environment={{
        env: effectiveEnv,
        overrides: { ...envOverrides },
      }}
    >
      {children}
    </PostOfficeBaseProvider>
  );
};
